import { useEffect, useRef, useState, type RefObject } from 'react';
import type React from 'react';

/** Shorter than the browser's own long-press, so the pin wins the press from its context menu. */
export const TOUCH_HOLD_MS = 400;
const TOUCH_SLOP_PX = 10;

/** True when focus came from the keyboard, not from a click or tap on the control. */
export const isKeyboardFocus = (element: Element): boolean => element.matches(':focus-visible');

interface TouchHoldPin {
  enabled: boolean;
  onPin: (pressed: DOMRect) => void;
  onRelease: () => void;
  /** The pinned box; a tap inside it keeps it open, so a long one can scroll. */
  box: RefObject<HTMLElement | null>;
}

/**
 * Press and hold to read: a touch has no hover, and a finger lifts before anyone can read, so a held
 * press keeps the tooltip up until the next tap. A quick tap stays the control's own, and the held
 * press does not also click or fire the right-click action (a hotbar slot clears on right-click).
 */
export function useTouchHoldPin({ enabled, onPin, onRelease, box }: TouchHoldPin) {
  const [pinned, setPinned] = useState(false);
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const heldThisPress = useRef(false);

  const endPress = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };
  useEffect(() => endPress, []);

  useEffect(() => {
    if (!pinned) return undefined;
    const release = (event: PointerEvent) => {
      if (event.target instanceof Node && box.current?.contains(event.target)) return;
      setPinned(false);
      onRelease();
    };
    window.addEventListener('pointerdown', release, true);
    return () => window.removeEventListener('pointerdown', release, true);
  }, [pinned, onRelease, box]);

  const swallow = (event: React.SyntheticEvent) => {
    if (!heldThisPress.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    pinned,
    /** Feed a touch's moves here: a finger that travels is scrolling, not holding. */
    onTouchMove: (event: React.PointerEvent) => {
      if (press.current && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > TOUCH_SLOP_PX) endPress();
    },
    pressHandlers: {
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        heldThisPress.current = false;
        endPress();
        if (event.pointerType !== 'touch' || !enabled) return;
        const pressed = event.currentTarget;
        press.current = {
          x: event.clientX,
          y: event.clientY,
          timer: setTimeout(() => {
            press.current = null;
            heldThisPress.current = true;
            setPinned(true);
            onPin(pressed.getBoundingClientRect());
          }, TOUCH_HOLD_MS),
        };
      },
      onPointerUp: endPress,
      onPointerCancel: endPress,
      onClickCapture: swallow,
      onContextMenuCapture: swallow,
    },
  };
}
